import { useMemo } from 'react';
import { LogEntry, VolumeBucket, LEVEL_KEYS } from '../types';

export function useChartData(logEntries: LogEntry[], chartZoomRange: { from: number; to: number } | null) {
  const filteredLogEntries = useMemo(() => {
    if (!chartZoomRange) return logEntries;
    const fromNs = chartZoomRange.from * 1_000_000;
    const toNs = chartZoomRange.to * 1_000_000;
    return logEntries.filter((e) => e.timestamp >= fromNs && e.timestamp <= toNs);
  }, [logEntries, chartZoomRange]);

  const chartTimeRange = useMemo((): { start: number; end: number } | null => {
    if (logEntries.length === 0) return null;
    let minMs = Infinity;
    let maxMs = -Infinity;
    for (const entry of logEntries) {
      const ms = entry.timestamp / 1_000_000;
      if (ms < minMs) minMs = ms;
      if (ms > maxMs) maxMs = ms;
    }
    const s = Math.floor(minMs);
    const e = Math.ceil(maxMs) + 1;
    return { start: s, end: Math.max(e, s + 2000) };
  }, [logEntries]);

  const volumeData = useMemo((): VolumeBucket[] => {
    if (!chartTimeRange) return [];

    const { start, end } = chartTimeRange;
    const rangeMs = end - start;
    if (rangeMs <= 0) return [];
    const bucketCount = Math.min(Math.max(Math.floor(rangeMs / 1000 / 2), 20), 80);
    const bucketSizeMs = rangeMs / bucketCount;

    const buckets: VolumeBucket[] = [];
    const seenLabels = new Map<string, number>();
    for (let i = 0; i < bucketCount; i++) {
      const bucketStart = start + i * bucketSizeMs;
      const d = new Date(bucketStart);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      const base = `${hh}:${mm}:${ss}`;
      const count = (seenLabels.get(base) || 0) + 1;
      seenLabels.set(base, count);
      const label = count === 1 ? base : `${base}.${count}`;
      buckets.push({
        timeLabel: label,
        debug: 0, info: 0, warn: 0, error: 0, critical: 0, unknown: 0,
      });
    }

    for (const entry of logEntries) {
      const entryMs = entry.timestamp / 1_000_000;
      const idx = Math.min(Math.floor((entryMs - start) / bucketSizeMs), bucketCount - 1);
      if (idx >= 0 && idx < bucketCount) {
        const lvl = entry.level as keyof VolumeBucket;
        if (lvl in buckets[idx] && typeof buckets[idx][lvl] === 'number') {
          (buckets[idx][lvl] as number)++;
        }
      }
    }

    return buckets;
  }, [logEntries, chartTimeRange]);

  const hasNonZeroVolume = useMemo(() => {
    return volumeData.some((b) =>
      b.debug > 0 || b.info > 0 || b.warn > 0 || b.error > 0 || b.critical > 0 || b.unknown > 0
    );
  }, [volumeData]);

  return {
    filteredLogEntries,
    chartTimeRange,
    volumeData,
    hasNonZeroVolume,
  };
}
